import { getThemeColor, HorizontalAlignment, LogManager, VerticalAlignment } from "@mcleod/core";
import { ImageName } from "@mcleod/images";
import { ComponentStyles, Label, Stepper } from "../..";
import { ComponentTypes } from "../../base/ComponentTypes";
import { Cursor } from "../../base/Cursor";
import { Image } from "../image/Image";
import { Panel } from "../panel/Panel";
import { StepPropDefinitions } from "./StepProps";

const log = LogManager.getLogger("components.stepper.Step");

export class Step extends Panel {
    private _caption: string;
    private _index: number;
    private _headingLabel: Label;
    private _stepLabel: Label;
    private _heading: Panel;
    private _headingImage: Image
    private _checkedImage: Image
    _selectedIndex: number;
    _visited: boolean;
    _selected: boolean;
    _checked: boolean;

    constructor(props) {
        super({ fillRow: true, rowBreak: false });
        this.createHeading();
        this.setProps(props);
    }

    override syncInnerComponentIds() {
        this.setInnerComponentId(this._heading, "heading");
        this.setInnerComponentId(this._headingLabel, "headingLabel");
        this.setInnerComponentId(this._stepLabel, "stepLabel");
        this.setInnerComponentId(this._headingImage, "headingImage");
    }

    private get stepper(): Stepper {
        return this.owner as Stepper;
    }

    set caption(value) {
        this._caption = value;
        this._headingLabel.text = value;
    }

    get caption() {
        return this._caption;
    }

    get index() {
        return this._index;
    }

    set index(value) {
        this._index = value;
        this._stepLabel.text = (value + 1).toString();
    }

    select() {
        this.stepper.selectedIndex = this.index;
    }

    get selected() {
        return this._selected;
    }

    set selected(value) {
        this._selected = value;
        if (value)
            this.visited = true;
        else
            this.renderStepLabel();
    }

    get visited() {
        return this._visited;
    }

    set visited(value) {
        this._visited = value;
        this.renderStepLabel();
    }

    get selectedIndex() {
        return this._selectedIndex;
    }

    set selectedIndex(value) {
        this._selectedIndex = value;
    }

    renderStepLabel() {
        this._stepLabel.backgroundColor = this.selected ? "primary" : "subtle.light";
        this._headingLabel.color = (this.selected || this.visited) ? "subtle.darker" : "subtle.light";
        if (this.stepper.enforceStepSequence) {
            if (this.visited && !this.selected && this._designer == null) {
                this.checked = true;
            }
        }
        this.heading.setClassIncluded(ComponentStyles.hoverShade, !this.selected && this.visited);
    }

    set checked(value: boolean) {
        this._checked = value;
        this._checkedImage.visible = value;
    }

    get checked() {
        return this._checked;
    }

    close() {
        this.parent.remove(this);
    }

    get heading() {
        return this._heading;
    }

    createHeading() {
        this._heading = new Panel({
            rowBreak: false,
            color: getThemeColor("subtle.light"),
            verticalAlign: VerticalAlignment.CENTER,
            borderRadius: 4
        });
        this._stepLabel = new Label({
            backgroundColor: "subtle.light",
            borderRadius: "50%",
            rowBreak: false,
            allowSelect: false,
            text: this.index?.toString(),
            width: 24,
            height: 24,
            marginRight: 4,
            align: HorizontalAlignment.CENTER,
            color: "subtle.reverse",
            cursor: Cursor.POINTER
        });
        this._headingLabel = new Label({
            text: this.caption,
            rowBreak: false,
            allowSelect: false,
            fontBold: true,
            cursor: Cursor.POINTER,
        });
        this._checkedImage = new Image({
            name: ImageName.check,
            color: "success",
            visible: false
        });
        this._stepLabel.addClickListener(() => this.select());
        this._headingLabel.addClickListener(() => this.select());
        this._heading.add(this._stepLabel);
        this._heading.add(this._headingLabel);
        this._heading.add(this._checkedImage);
    }

    _imageUpdated(image: Image) {
        if (this._headingImage == null) {
            this._headingImage = new Image({ id: this.createInnerComponentId("headingImage"), rowBreak: false, marginRight: 4 });
            this._heading.insert(this._headingImage, this._heading.indexOf(this._headingLabel));
        }
        if (this._headingImage._element.parentElement != null)
            this._headingImage._element.parentElement.replaceChild(image._element, this._headingImage._element);
        this._headingImage._element = image._element;
    }

    getPropertyDefinitions() {
        return StepPropDefinitions.getDefinitions();
    }

    override get serializationName() {
        return "step";
    }

    override get properName(): string {
        return "Step";
    }

    override getBasicValue(): any {
        return this.index;
    }
}

ComponentTypes.registerComponentType("step", {
    constructor: Step.prototype.constructor
});
